import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import { deleteLicenseLeaflet, replaceLicenseLeaflet } from '../licenseLeafletFile';

const QK = ['admin', 'license-leaflets'] as const;

export interface LicenseLeafletUse {
  territory: string;
  scope: 'continental' | 'maritima' | 'submarina';
}

export interface LicenseLeafletRow {
  blobName: string;
  uses: LicenseLeafletUse[];
  note: string | null;
  present: boolean;
  contentLength: number | null;
  lastModified: string | null;
}

export interface LicenseLeafletOrphan {
  name: string;
  contentLength: number;
  lastModified: string | null;
  contentType: string | null;
  manageable: boolean;
}

export interface LicenseLeafletInventory {
  leaflets: LicenseLeafletRow[];
  orphans: LicenseLeafletOrphan[];
}

export function useLicenseLeafletsAdmin() {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const { data, error } = await apiClient.get<LicenseLeafletInventory>('/ops/license-leaflets');
      if (error) throw new Error(error.message);
      if (!data) throw new Error('Sin respuesta del inventario de folletos');
      return data;
    },
    staleTime: 15_000,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: QK });
  };

  const replace = useMutation({
    mutationFn: (input: { blobName: string; file: File }) =>
      replaceLicenseLeaflet(input.blobName, input.file),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (blobName: string) => deleteLicenseLeaflet(blobName),
    onSuccess: refresh,
  });

  return { list, replace, remove };
}
